/**
 * Sprint 26.0 — task hooks backing the agent page Queue + Plan lenses.
 *
 * The clean-build replacement for the removed focus-model hooks
 * (use-focus-model / use-agent-tasks). One noun: Task. The brain snapshot
 * is the composite read; the per-lens lists (queue / plan) are fetched lazily;
 * every mutation invalidates the snapshot + both lens lists so the page stays
 * truthful after any re-rank / triage / complete / drop.
 *
 * There is NO focus status and NO sub-agent spawn — "make current" is
 * `moveTaskToTop` (rank #1 = lowest sort_order).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";
import type { AgentBrainSnapshot, Task, TaskStatus } from "../../lib/types";

const POLL_MS = 30_000;
const STALE_MS = 15_000;

/** Invalidate every task-backed query for the agent page after a mutation. */
function invalidateTasks(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["agent-brain-snapshot"] });
  qc.invalidateQueries({ queryKey: ["agent-tasks-26"] });
  qc.invalidateQueries({ queryKey: ["agent-task-plan"] });
  qc.invalidateQueries({ queryKey: ["needs-you"] });
  qc.invalidateQueries({ queryKey: ["workforce-status"] });
}

/** The brain snapshot — one composite read backing the whole agent page. */
export function useAgentBrainSnapshot(agentId: string | undefined, enabled = true) {
  return useQuery<AgentBrainSnapshot>({
    queryKey: ["agent-brain-snapshot", agentId],
    queryFn: () => mcpClient.getAgentBrainSnapshot(agentId as string),
    enabled: enabled && !!agentId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** The Queue lens — the full ranked (sort_order) list. Fetched lazily on expand. */
export function useAgentTaskQueue(agentId: string | undefined, enabled = true) {
  return useQuery<Task[]>({
    queryKey: ["agent-tasks-26", agentId, "queue"],
    queryFn: () => (agentId ? mcpClient.listTasks(agentId, { lens: "queue" }) : []),
    enabled: enabled && !!agentId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** The Plan lens — the nested sub-task tree (each task carries `children`). */
export function useAgentTaskPlan(
  agentId: string | undefined,
  opts: { status?: TaskStatus; include_archived?: boolean } = {},
  enabled = true,
) {
  return useQuery<Task[]>({
    queryKey: ["agent-task-plan", agentId, opts.status ?? null, opts.include_archived ?? false],
    queryFn: () => (agentId ? mcpClient.listTaskPlan(agentId, opts) : []),
    enabled: enabled && !!agentId,
    staleTime: STALE_MS,
    refetchInterval: POLL_MS,
    refetchOnWindowFocus: true,
  });
}

/** Make a task current — move it to rank #1 (the only "make current"). */
export function useMoveTaskToTop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => mcpClient.moveTaskToTop(taskId),
    onSuccess: () => invalidateTasks(qc),
  });
}

/** Re-rank / reparent a task (drag). `to_rank` re-orders; `parent_task_id` reparents. */
export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      task_id: string;
      to_top?: boolean;
      to_rank?: number;
      parent_task_id?: string | null;
    }) => mcpClient.moveTask(params),
    onSuccess: () => invalidateTasks(qc),
  });
}

/** Triage an inbox task into the ranked queue (inbox → queued). */
export function useTriageTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (taskId: string) => mcpClient.triageTask(taskId),
    onSuccess: () => invalidateTasks(qc),
  });
}

export function useCompleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ agentId, taskId, summary }: { agentId: string; taskId: string; summary?: string }) =>
      mcpClient.completeTask(agentId, taskId, summary),
    onSuccess: () => invalidateTasks(qc),
  });
}

export function useDropTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, reason }: { taskId: string; reason?: string }) =>
      mcpClient.dropTask(taskId, reason),
    onSuccess: () => invalidateTasks(qc),
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      agent_id: string;
      title: string;
      body?: string;
      status?: "inbox" | "queued";
      parent_task_id?: string;
    }) => mcpClient.createTask(params),
    onSuccess: () => invalidateTasks(qc),
  });
}
